import React, { useState, useEffect } from "react";
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { useApi } from '../../hooks/useApi';
import { API_ENDPOINTS } from '../../constants/api';
import { validateTask } from '../../utils/validation';
import axiosInstance from '../../api/Axios';
import { TASK_CATEGORIES } from "../../constants/status";
import useNotification from "../../hooks/useNotification";

const EMPTY_TASK = {
  title: '',
  description: '',
  developer: '',
  project: '',
  hours: 0,
  category: '',
};

const AddTaskModal = ({
  isOpen,
  onClose,
  onSuccess,
  task,
  developers,
  projects,
  categories,
}) => {
  const [form, setForm] = useState(EMPTY_TASK);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useNotification();
  const { postData: createTask } = useApi(API_ENDPOINTS.CREATE_TASK);

  useEffect(() => {
    if (isOpen) {
      setForm(task ? { ...task } : { ...EMPTY_TASK });
      setErrors({});
    }
  }, [isOpen, task]);

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleSave = async () => {
    const validation = validateTask(form);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    if (!form.developer) {
      setErrors({ ...errors, developer: 'Необходимо выбрать разработчика' });
      return;
    }
    if (!form.project) {
      setErrors({ ...errors, project: 'Необходимо выбрать проект' });
      return;
    }
    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      if (task && task._id) {
        // Edit
        const updatedTaskData = { ...form, editedAt: now };
        await axiosInstance.put(`${API_ENDPOINTS.UPDATE_TASK}/${task._id}`, updatedTaskData);
        showSuccess('Задача успешно обновлена');
      } else {
        // Create
        const newTaskData = {
          ...form,
          category: TASK_CATEGORIES.ITEMS2,
          createdAt: now,
          updatedAt: now,
        };
        await createTask(newTaskData);
        showSuccess('Задача успешно создана');
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      showError('Ошибка при сохранении задачи');
    } finally {
      setIsLoading(false);
    }
  };

  const developerOptions = developers
    .filter(dev => dev.id !== "all")
    .map(dev => ({ id: dev.id, name: dev.name }));
  const projectOptions = projects
    .filter(proj => proj.id !== "all")
    .map(proj => ({ id: proj.id, name: proj.name }));
  const isCompletedTask = task?.category === TASK_CATEGORIES.ITEMS3;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={task ? "Редактировать задачу" : "Добавить новую задачу"}
      size="medium"
    >
      <div className="space-y-4">
        <Input
          label="Название задачи"
          value={form.title}
          onChange={e => handleInputChange('title', e.target.value)}
          placeholder="Введите название задачи"
          error={errors.title}
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Описание
            <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            value={form.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Введите описание задачи"
            className={`w-full px-3 py-2 border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.description ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
            rows="3"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>
        <Select
          label="Разработчик"
          value={form.developer}
          onChange={e => handleInputChange('developer', e.target.value)}
          options={developerOptions}
          placeholder="Выберите разработчика"
          error={errors.developer}
          required
        />
        <Select
          label="Проект"
          value={form.project}
          onChange={e => handleInputChange('project', e.target.value)}
          options={projectOptions}
          placeholder="Выберите проект"
          error={errors.project}
          required
        />
        <Input
          label="Количество часов"
          type="number"
          value={form.hours}
          onChange={e => handleInputChange('hours', e.target.value)}
          placeholder="Введите количество часов"
          error={errors.hours}
          disabled={isCompletedTask}
          required
        />
        <div className="flex justify-end space-x-2 mt-6">
          <Button onClick={onClose} variant="secondary" disabled={isLoading}>
            Отмена
          </Button>
          <Button onClick={handleSave} variant="primary" loading={isLoading}>
            {task ? "Сохранить" : "Добавить"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddTaskModal; 